import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';
import { LocationData } from './../types/locationData';

const ENDPOINT_URL = '/users/log-location';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type LocationStatus = paths[url]['post']['requestBody']['content']['application/json'];
export type LocationStatusSuccessResponse = paths[url]['post']['responses']['200']['content']['application/json'];

export interface InsertLogLocationSuccessResponse {
    status: number;
    success: boolean;
    message: string;
    data: LocationData;
}

export const useLogLocation = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { setLastLocationSaved, lastLocationSaved } = useData();

    const request = async (props: LocationStatus) => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            const data: InsertLogLocationSuccessResponse = await response.json();

            if (!response.ok) {
                const errorMessage = data.message || 'An error occurred';
                const error = new Error(errorMessage) as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const lastUpdated = new Date().toISOString();

            setLastLocationSaved({
                coordinates: [data?.data?.usr_location?.latitude, data?.data?.usr_location?.longitude],
                lastUpdated: lastUpdated,
                locationAccessGranted: lastLocationSaved?.locationAccessGranted ?? false,
            });

        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, success };
};
